import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { AUTH_KEY, getAuth } from "../lib/clientId";
import { setAuthToken, authLogin, authRegister, authVerify, authResend } from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => getAuth()?.user || null);
  const [modal, setModal] = useState({ open: false, tab: "login", email: "", reason: "" });

  useEffect(() => {
    const a = getAuth();
    if (a?.token) setAuthToken(a.token);
  }, []);

  const persist = (data) => {
    localStorage.setItem(AUTH_KEY, JSON.stringify({ token: data.token, user: data.user }));
    setAuthToken(data.token);
    setUser(data.user);
  };

  const openAuth = useCallback((tab = "login", email = "", reason = "") => setModal({ open: true, tab, email, reason }), []);
  const closeAuth = useCallback(() => setModal((m) => ({ ...m, open: false })), []);

  const login = async (email, password) => {
    const data = await authLogin(email, password);
    persist(data);
    if (data.user.verified) { toast.success("Signed in"); setTimeout(() => window.location.reload(), 300); }
    return data;
  };

  const register = async (email, password) => {
    const data = await authRegister(email, password);
    persist(data);
    if (!data.needsVerification) { toast.success("Account created"); setTimeout(() => window.location.reload(), 300); }
    return data;
  };

  const verify = async (email, code) => {
    const data = await authVerify(email, code);
    const a = getAuth();
    localStorage.setItem(AUTH_KEY, JSON.stringify({ token: a?.token, user: data.user }));
    setUser(data.user);
    toast.success("Email verified");
    setTimeout(() => window.location.reload(), 300);
    return data;
  };

  const resendCode = async (email) => authResend(email);

  const logout = useCallback(() => {
    localStorage.removeItem(AUTH_KEY);
    setAuthToken(null);
    setUser(null);
    setTimeout(() => window.location.reload(), 200);
  }, []);

  // Returns true if the user may perform a save action; otherwise opens the auth flow.
  const requireAuth = useCallback((label) => {
    if (!user) {
      openAuth("signup", "", label || "save your data");
      return false;
    }
    if (!user.verified) {
      openAuth("verify", user.email, "");
      toast.info("Verify your email to save your data");
      return false;
    }
    return true;
  }, [user, openAuth]);

  return (
    <AuthContext.Provider value={{ user, isAuthed: !!user, modal, openAuth, closeAuth, login, register, verify, resendCode, logout, requireAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
